/**
 * ============================================================
 * wishlist.js  —  User-based Wishlist for Shopify
 * ============================================================
 *
 * Strategy:
 *  • Logged-in customers  → persists to customer metafield
 *    `custom.wishlist` (type: list.product_reference) via
 *    Shopify's Storefront API customerUpdate mutation.
 *    The metafield value is read from a <script> tag injected
 *    by Liquid (see wishlist-init-data.liquid snippet).
 *
 *  • Guest visitors → falls back to localStorage so the UX
 *    still works. On login the guest list is merged into the
 *    customer metafield automatically.
 *
 * Required setup (one-time):
 *  1. Create a customer metafield definition in Shopify Admin:
 *       Namespace : custom
 *       Key       : wishlist
 *       Type      : List of products (list.product_reference)
 *
 *  2. Add the Storefront API token + customer data via Liquid.
 *     Include {% render 'wishlist-init-data' %} in theme.liquid
 *     before this script loads.
 *
 *  3. Upload this file to theme Assets as wishlist.js
 * ============================================================
 */

(() => {
  'use strict';

  /* ----------------------------------------------------------
     Config — read values injected by wishlist-init-data.liquid
  ---------------------------------------------------------- */
  const CONFIG            = window.__wishlistConfig || {};
  const STOREFRONT_TOKEN  = CONFIG.storefrontToken  || '';
  const STOREFRONT_URL    = CONFIG.storefrontUrl
                            || `https://${window.Shopify?.shop}/api/2024-04/graphql.json`;
  const CUSTOMER_TOKEN    = CONFIG.customerToken    || null;
  const IS_LOGGED_IN      = Boolean(CUSTOMER_TOKEN && STOREFRONT_TOKEN);
  const LOCALE            = CONFIG.locale           || 'en-IN';

  // localStorage key — used for guests AND as an optimistic write-buffer
  const LS_KEY = 'shopify_wishlist_ids';

  /* ----------------------------------------------------------
     Internal cache  (array of plain product ID strings)
  ---------------------------------------------------------- */
  let _cache = null;   // null = not yet initialised

  /* ----------------------------------------------------------
     Utilities
  ---------------------------------------------------------- */
  function dispatch(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  function lsGet() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
    catch { return []; }
  }

  function lsSet(ids) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); }
    catch { /* private browsing / storage full */ }
  }

  function gidToId(gid) {
    // "gid://shopify/Product/123456" → "123456"
    return String(gid).split('/').pop();
  }

  function idToGid(id) {
    return `gid://shopify/Product/${id}`;
  }

  /* ----------------------------------------------------------
     Storefront API
  ---------------------------------------------------------- */
  async function storefrontFetch(query, variables = {}) {
    const res = await fetch(STOREFRONT_URL, {
      method : 'POST',
      headers: {
        'Content-Type'                      : 'application/json',
        'X-Shopify-Storefront-Access-Token' : STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error(`Storefront API HTTP ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
  }

  /* ----------------------------------------------------------
     Load wishlist from customer metafield
  ---------------------------------------------------------- */
  async function loadFromMetafield() {
    // Fast path: Liquid already rendered the IDs into the page
    if (Array.isArray(CONFIG.wishlistIds)) {
      return CONFIG.wishlistIds.map(String);
    }

    const query = `
      query GetWishlist($token: String!) {
        customer(customerAccessToken: $token) {
          metafield(namespace: "custom", key: "wishlist") {
            value
          }
        }
      }
    `;
    const data = await storefrontFetch(query, { token: CUSTOMER_TOKEN });
    const raw  = data?.customer?.metafield?.value;
    if (!raw) return [];
    try {
      return JSON.parse(raw).map(gidToId);
    } catch {
      return [];
    }
  }

  /* ----------------------------------------------------------
     Save wishlist to customer metafield
  ---------------------------------------------------------- */
  async function saveToMetafield(ids) {
    const mutation = `
      mutation UpdateWishlist($token: String!, $input: CustomerUpdateInput!) {
        customerUpdate(customerAccessToken: $token, customer: $input) {
          customer { id }
          customerUserErrors { field message }
        }
      }
    `;
    await storefrontFetch(mutation, {
      token: CUSTOMER_TOKEN,
      input: {
        metafields: [{
          namespace : 'custom',
          key       : 'wishlist',
          type      : 'list.product_reference',
          value     : JSON.stringify(ids.map(idToGid)),
        }],
      },
    });
  }

  /* ----------------------------------------------------------
     WishlistStore — public interface
  ---------------------------------------------------------- */
  const WishlistStore = {

    /**
     * Must be called once on page load.
     * Loads data from metafield (logged-in) or localStorage (guest)
     * and merges any guest items into the account on first login.
     */
    async init() {
      if (IS_LOGGED_IN) {
        try {
          const serverIds = await loadFromMetafield();
          const guestIds  = lsGet();

          // Merge: union of server list + any items saved while guest
          const merged = [...new Set([...serverIds, ...guestIds])];

          if (guestIds.length > 0) {
            lsSet([]);                           // clear the guest buffer
            await saveToMetafield(merged);       // persist merged list
          }

          _cache = merged;
        } catch (err) {
          console.warn('[Wishlist] Metafield load failed, using localStorage:', err);
          _cache = lsGet();
        }
      } else {
        _cache = lsGet();
      }

      dispatch('wishlist:loaded', { ids: [..._cache] });
      return _cache;
    },

    getAll() {
      return _cache !== null ? [..._cache] : lsGet();
    },

    has(productId) {
      return this.getAll().includes(String(productId));
    },

    async add(productId) {
      const id   = String(productId);
      const list = this.getAll();
      if (list.includes(id)) return;

      const next = [...list, id];
      _cache = next;
      lsSet(next);                                // optimistic local update
      dispatch('wishlist:add',    { productId: id });
      dispatch('wishlist:change', { productId: id, wishlisted: true });

      if (IS_LOGGED_IN) {
        try { await saveToMetafield(next); }
        catch (err) { console.warn('[Wishlist] Save failed:', err); }
      }
    },

    async remove(productId) {
      const id   = String(productId);
      const next = this.getAll().filter((i) => i !== id);
      _cache = next;
      lsSet(next);
      dispatch('wishlist:remove', { productId: id });
      dispatch('wishlist:change', { productId: id, wishlisted: false });

      if (IS_LOGGED_IN) {
        try { await saveToMetafield(next); }
        catch (err) { console.warn('[Wishlist] Save failed:', err); }
      }
    },

    async toggle(productId) {
      this.has(productId)
        ? await this.remove(productId)
        : await this.add(productId);
    },
  };

  /* ----------------------------------------------------------
     UI helpers
  ---------------------------------------------------------- */
  function syncButton(btn, productId) {
    const active = WishlistStore.has(productId);
    btn.classList.toggle('is-wishlisted', active);
    btn.setAttribute('aria-label',   active ? 'Remove from wishlist' : 'Add to wishlist');
    btn.setAttribute('aria-pressed', String(active));
    const path = btn.querySelector('path');
    if (path) path.setAttribute('fill', active ? 'currentColor' : 'none');
  }

  function animateButton(btn) {
    btn.classList.add('wishlist-btn--pop');
    btn.addEventListener('animationend', () => btn.classList.remove('wishlist-btn--pop'), { once: true });
  }

  function bindButton(btn) {
    const productId = btn.dataset.wishlistBtn || btn.dataset.productId;
    if (!productId) return;

    syncButton(btn, productId);

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      btn.disabled = true;
      await WishlistStore.toggle(productId);
      syncButton(btn, productId);
      animateButton(btn);
      btn.disabled = false;

      if (!IS_LOGGED_IN) showLoginNudge(btn);
    });
  }

  function initWishlistButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(bindButton);

    document.addEventListener('wishlist:change', (e) => {
      document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
        const id = btn.dataset.wishlistBtn || btn.dataset.productId;
        if (id && String(id) === String(e.detail.productId)) syncButton(btn, id);
      });
    });

    document.addEventListener('wishlist:loaded', () => {
      document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
        const id = btn.dataset.wishlistBtn || btn.dataset.productId;
        if (id) syncButton(btn, id);
      });
    });
  }

  /* ----------------------------------------------------------
     Login nudge tooltip for guests
  ---------------------------------------------------------- */
  function showLoginNudge(anchor) {
    const old = document.getElementById('wl-nudge');
    if (old) old.remove();

    const el = document.createElement('div');
    el.id = 'wl-nudge';
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    el.innerHTML = `Saved locally. <a href="/account?return_url=${returnUrl}">Log in</a> to sync across devices.`;

    Object.assign(el.style, {
      position   : 'absolute',
      background : '#1a1a1a',
      color      : '#fff',
      fontSize   : '12px',
      padding    : '7px 12px',
      borderRadius: '6px',
      whiteSpace : 'nowrap',
      zIndex     : '9999',
      boxShadow  : '0 2px 10px rgba(0,0,0,.3)',
      pointerEvents: 'auto',
    });

    document.body.appendChild(el);

    const rect = anchor.getBoundingClientRect();
    el.style.top  = `${rect.bottom + window.scrollY + 8}px`;
    el.style.left = `${Math.max(8, rect.left + window.scrollX)}px`;

    const link = el.querySelector('a');
    if (link) Object.assign(link.style, { color: '#f6c90e', textDecoration: 'underline' });

    setTimeout(() => el.remove(), 4500);
    document.addEventListener('click', () => el.remove(), { once: true });
  }

  /* ----------------------------------------------------------
     Counter badge  [data-wishlist-count]
  ---------------------------------------------------------- */
  function initWishlistCounter() {
    function update() {
      const n = WishlistStore.getAll().length;
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent  = n;
        el.style.display = n > 0 ? '' : 'none';
      });
    }
    update();
    ['wishlist:add', 'wishlist:remove', 'wishlist:loaded'].forEach((ev) =>
      document.addEventListener(ev, update)
    );
  }

  /* ----------------------------------------------------------
     Wishlist Page renderer  (/pages/wishlist)
  ---------------------------------------------------------- */
  async function renderWishlistPage() {
    if (!document.querySelector('[data-wishlist-page]')) return;

    const emptyEl    = document.querySelector('[data-wishlist-empty]');
    const loginWall  = document.querySelector('[data-wishlist-login-wall]');
    const grid       = document.querySelector('[data-wishlist-grid]');

    if (!IS_LOGGED_IN && loginWall) loginWall.style.display = '';

    const ids = WishlistStore.getAll();

    if (ids.length === 0) {
      if (emptyEl) emptyEl.style.display = '';
      if (grid) grid.innerHTML = '';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (grid) grid.innerHTML = '<p class="wishlist-loading">Loading your wishlist…</p>';

    // Fetch via Storefront API nodes query
    let products = [];
    try {
      const query = `
        query WishlistProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id title handle
              onlineStoreUrl
              featuredImage { url altText }
              priceRange { minVariantPrice { amount currencyCode } }
              variants(first: 1) { edges { node { id availableForSale } } }
            }
          }
        }
      `;
      const data = await storefrontFetch(query, { ids: ids.map(idToGid) });
      products = (data?.nodes || []).filter(Boolean);
    } catch {
      // Fallback: search suggest (no Storefront token needed)
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`/search/suggest.json?q=${id}&resources[type]=product&resources[limit]=1`)
            .then((r) => r.json()).catch(() => null)
        )
      );
      products = results
        .flatMap((r) => r?.resources?.results?.products || [])
        .filter(Boolean)
        .map((p) => ({
          id           : `gid://shopify/Product/${p.id}`,
          title        : p.title,
          handle       : p.handle,
          onlineStoreUrl: p.url,
          featuredImage : { url: p.featured_image, altText: p.title },
          priceRange    : { minVariantPrice: { amount: (p.price / 100).toFixed(2), currencyCode: 'INR' } },
        }));
    }

    if (!grid) return;

    if (products.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      return;
    }

    grid.innerHTML = products.map((p) => {
      const rawId   = gidToId(p.id);
      const url     = p.onlineStoreUrl || `/products/${p.handle}`;
      const img     = p.featuredImage?.url   || '';
      const alt     = p.featuredImage?.altText || p.title;
      const amount  = parseFloat(p.priceRange?.minVariantPrice?.amount || 0);
      const curr    = p.priceRange?.minVariantPrice?.currencyCode || 'INR';
      return `
        <div class="wishlist-card" data-product-id="${rawId}">
          <button class="wishlist-card__remove" data-wishlist-btn="${rawId}" aria-label="Remove from wishlist">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <a href="${url}" class="wishlist-card__image-link">
            <img src="${img}" alt="${alt}" class="wishlist-card__image" loading="lazy">
          </a>
          <div class="wishlist-card__info">
            <a href="${url}" class="wishlist-card__title">${p.title}</a>
            <p class="wishlist-card__price">${formatMoney(amount, curr)}</p>
            <a href="${url}" class="wishlist-card__cta button">View Product</a>
          </div>
        </div>`;
    }).join('');

    // Bind remove buttons; animate card out on removal
    grid.querySelectorAll('.wishlist-card__remove').forEach(bindButton);

    document.addEventListener('wishlist:remove', (e) => {
      const card = grid.querySelector(`.wishlist-card[data-product-id="${e.detail.productId}"]`);
      if (!card) return;
      Object.assign(card.style, { transition: 'opacity .25s, transform .25s', opacity: '0', transform: 'scale(0.95)' });
      setTimeout(() => {
        card.remove();
        if (!grid.querySelector('.wishlist-card') && emptyEl) emptyEl.style.display = '';
      }, 260);
    });
  }

  /* ----------------------------------------------------------
     Money formatter
  ---------------------------------------------------------- */
  function formatMoney(amount, currency = 'INR') {
    try {
      return new Intl.NumberFormat(LOCALE, {
        style                : 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `₹${Number(amount).toFixed(0)}`;
    }
  }

  /* ----------------------------------------------------------
     Boot
  ---------------------------------------------------------- */
  async function boot() {
    await WishlistStore.init();
    initWishlistButtons();
    initWishlistCounter();
    await renderWishlistPage();
  }

  document.addEventListener('DOMContentLoaded', boot);

  // Expose for use in Liquid / other scripts
  window.WishlistStore = WishlistStore;

})();
